import { feedRowHasDate, feedRowHasDuration, feedRowHasViewCount, feedScrape, type FeedScrape } from '../feed/listing.ts';
import { isChannelId } from '../links/routes.ts';
import type { VideoSummary } from '../model/video.ts';
import type { ParseError } from '../result.ts';

/**
 * §8's Subscriptions row — `GET /api/v1/auth/feed`, its paging, and the one thing about it that §8
 * gets backwards.
 *
 * ## `notifications` and `videos` are one list, not two
 *
 * §8 says "Render `videos`; `notifications` is an optional 'new since last visit' band", which reads
 * as two independent lists where the second is a bonus. Measured on the test account (3 subscriptions,
 * 86 rows of feed) it is neither:
 *
 * ```
 * GET /api/v1/auth/feed?max_results=N     →  notifications.length + videos.length === N   exactly
 *   N =   1 →  0 + 1        N =  20 →  17 +  3        N =  60 →  30 + 30
 *   N =   2 →  1 + 1        N =  30 →  26 +  4        N =  86 →  30 + 56
 *   N =   5 →  4 + 1        N =  40 →  30 + 10        N = 100 →  30 + 56   ← the whole feed is 86
 * ```
 *
 * Seventeen values of `max_results` from 1 to 1000, and the two arrays sum to it every time (until the
 * feed runs out) with **zero overlap** between them. So `max_results` is a **page size over one merged
 * window**, and `notifications` is the subset of *that window* the instance has flagged — not a
 * separate list with a size of its own. Invidious builds it that way: it takes the window, partitions
 * out the rows whose ids are in the account's notification set, and returns the two halves.
 *
 * **Rendering only `videos` therefore does not truncate the feed, it puts a hole in the middle of
 * it.** On the default page size the top row is a video, the next thirty are notifications, and the
 * ten `videos` after that are older than all of them — so a page showing `videos` alone shows one
 * upload from today, then nothing for seventeen days, then ten older uploads. Nothing about that looks
 * like a bug from the outside; it looks like the channels were quiet.
 *
 * So this page renders **the merge**, ordered by `published` descending, and carries the flag as a
 * property of a row. Both arrays arrive already sorted that way (verified), so the merge is a
 * two-pointer pass rather than a re-sort — see {@link foldSubscriptionFeed} for why that distinction
 * is not pedantry.
 *
 * ## The flag is not "since your last visit", and nothing here can clear it
 *
 * Three consecutive reads returned `notifications: 30` unchanged, so reading the feed does not consume
 * it. The set is server-side state that Invidious's *own web UI* clears when you open its
 * subscriptions page; no `/api/v1/auth/*` endpoint does, and this build's grant could not call one if
 * there were. On the test account it had accumulated thirty rows spanning **seventeen days** — while
 * the single newest upload in the feed was *not* in it.
 *
 * That kills the "new since last visit" reading twice over: the flag is older than a visit and misses
 * the newest row. A **NEW** badge drawn from it would be false on both ends. What it genuinely is:
 * the number Invidious's own navbar shows — this measured that badge as **30** against three
 * subscriptions and recorded it as "a feed count, not a subscription count". It is neither. It is
 * exactly this: `notifications.length`.
 *
 * So the count is reported as what it is, with the row marked and no claim about recency.
 */

export const SUBSCRIPTION_FEED_PATH = '/api/v1/auth/feed';
export const SUBSCRIPTIONS_PATH = '/api/v1/auth/subscriptions';

/**
 * `POST`/`DELETE /api/v1/auth/subscriptions/{ucid}` — subscribe and unsubscribe share the path.
 * Measured (2026-07-31, ~20 calls): both answer **`204` with an empty body and both are idempotent**
 * — a second subscribe does not duplicate the row (unlike a playlist add) and unsubscribing a channel
 * that is not subscribed is `204` too, so a retry cannot fail differently. A malformed id, a handle
 * *and a well-shaped UCID that names no channel* are all refused
 * `403 {"error":"Deleted or invalid channel"}` — this endpoint validates **existence**, where
 * history's `POST` validates only shape.
 *
 * `null` for anything that is not a UCID, the history lesson applied even though the stakes here are
 * smaller: an empty id builds a path that answers a routing miss dressed as an authorization failure
 * (`403 … 'POST:/api/v1/auth/subscriptions/' was not found`), not a destroy-all — measured, list
 * unchanged — but "signed out" about a bug in our URL building is still the wrong sentence.
 */
export function subscriptionChangePath(channelId: string): string | null {
  return isChannelId(channelId) ? `${SUBSCRIPTIONS_PATH}/${channelId}` : null;
}

/**
 * Pages are 1-based and **`page=0` is not an empty page, it is a `403`** — with a leaked Postgres
 * error, `{"error":"OFFSET must not be negative"}`. `page=-1` the same, and `page=1000000000` answers
 * `403 {"error":"Arithmetic overflow"}`. That is the fourth unrelated meaning `403` carries in this
 * API (media expiry · signed out · endpoint disabled · this), and it is the reason
 * {@link subscriptionFeedWireParams} clamps rather than trusting a caller.
 *
 * A non-numeric `page` is *not* refused, incidentally: `page=abc`, `page=` and `page=1.5` all answer
 * page 1. Only the arithmetic is strict.
 */
export const SUBSCRIPTION_FEED_FIRST_PAGE = 1;

/**
 * 40, which is the instance's own default (a bare call returns 40 rows) and not a number chosen here.
 *
 * Worth keeping rather than raising: this endpoint returns **full listing rows**, ~1.3 KB each, so 40
 * rows is ~52 KB and the whole 86-row feed is 112 KB. A "just fetch everything" page on an account
 * with 200 subscriptions would be several megabytes of JSON before a single thumbnail.
 */
export const SUBSCRIPTION_FEED_PAGE_SIZE = 40;

/**
 * The wire parameters, with both clamps applied — the ones that keep the two silent failures above
 * unreachable rather than merely unlikely.
 *
 * `max_results=0` and `max_results=-1` answer **`200 {"notifications":[],"videos":[]}`**, which is
 * byte-identical to "you have no subscriptions" and to "you are past the last page". So an unclamped
 * page size does not error — it renders an empty Subscriptions page to somebody who has 86 videos
 * waiting, and there is nothing anywhere to report. Clamped here because this is the one place every
 * request passes through.
 */
export function subscriptionFeedWireParams(
  page: number,
  maxResults: number = SUBSCRIPTION_FEED_PAGE_SIZE,
): Readonly<Record<string, string | null>> {
  const safePage = Number.isFinite(page)
    ? Math.max(SUBSCRIPTION_FEED_FIRST_PAGE, Math.floor(page))
    : SUBSCRIPTION_FEED_FIRST_PAGE;
  const safeMax = Number.isFinite(maxResults)
    ? Math.max(1, Math.floor(maxResults))
    : SUBSCRIPTION_FEED_PAGE_SIZE;

  return { max_results: String(safeMax), page: String(safePage) };
}

/** One row of the merged feed: a video, plus whether the instance had it flagged. */
export interface SubscriptionFeedRow {
  readonly video: VideoSummary;
  /**
   * The row was in `notifications` rather than in `videos`. Named for the mechanism rather than for a
   * meaning it does not have — see this module's note on why it is not "new".
   */
  readonly notified: boolean;
}

/** One response, already merged. The parser's output; {@link foldSubscriptionFeed} joins several. */
export interface SubscriptionFeedPage {
  readonly rows: readonly SubscriptionFeedRow[];
  /** R4 — rows the parser could not read, counted rather than lost. */
  readonly dropped: readonly ParseError[];
  /**
   * Whether the envelope itself was wrong — an array where `{notifications, videos}` belongs, or
   * neither key present. Distinct from "both arrays were empty", which is a real and reachable state
   * (an account with no subscriptions, and every page past the last one).
   */
  readonly malformed: boolean;
}

/**
 * Merge the two arrays of one response into the window they were split out of.
 *
 * A **two-pointer merge, not a sort**, and the difference is visible in exactly the case that matters.
 * Both arrays arrive descending by `published` (verified on a 40-row window: monotone in both), so a
 * merge reconstructs the original window's order exactly, including ties — whereas a comparison sort
 * over the concatenation would be free to interleave equal timestamps any way it liked. Two uploads in
 * the same second is not hypothetical: this feed had a pair 3 minutes apart from one channel and the
 * instance's `published` is second-resolution.
 *
 * A row with no `published` at all sorts last rather than first, since `null` here means "we could not
 * read the date" and hoisting an unreadable row to the top of a chronological list is the loudest
 * possible way to render an absence.
 */
export function mergeSubscriptionFeedRows(
  notifications: readonly VideoSummary[],
  videos: readonly VideoSummary[],
): readonly SubscriptionFeedRow[] {
  const rows: SubscriptionFeedRow[] = [];
  let n = 0;
  let v = 0;

  const at = (video: VideoSummary | undefined): number =>
    video === undefined || video.publishedEpochSec === null ? -Infinity : video.publishedEpochSec;

  while (n < notifications.length || v < videos.length) {
    const notification = notifications[n];
    const video = videos[v];
    if (notification === undefined) {
      if (video !== undefined) rows.push({ video, notified: false });
      v += 1;
      continue;
    }
    if (video === undefined) {
      rows.push({ video: notification, notified: true });
      n += 1;
      continue;
    }
    // `>=` keeps the notification first on a tie, which is the arbitrary half of the decision. The
    // non-arbitrary half is that it is *stable*: the same tie resolves the same way on every page.
    if (at(notification) >= at(video)) {
      rows.push({ video: notification, notified: true });
      n += 1;
    } else {
      rows.push({ video, notified: false });
      v += 1;
    }
  }

  return rows;
}

/* ------------------------------------------------------------------------------------------------
 * Paging
 * ---------------------------------------------------------------------------------------------- */

/** Several pages, folded into the one list a page renders. */
export interface SubscriptionListing {
  readonly rows: readonly SubscriptionFeedRow[];
  /** How many of `rows` the instance had flagged — the number Invidious's own navbar shows. */
  readonly notified: number;
  /** Rows dropped by a later page because an earlier one already had them. See {@link foldSubscriptionFeed}. */
  readonly duplicates: number;
  readonly dropped: number;
  readonly scrape: FeedScrape;
}

/**
 * Concatenate the pages, keeping the **first** occurrence of a video id.
 *
 * **The dedupe is justified by the mechanism rather than by an observed duplicate, and that is worth
 * being explicit about.** A five-page sweep at `max_results=20` returned 86 rows, 86 distinct, with no
 * repetition within a page or across pages — genuinely unlike `/api/v1/playlists/{plid}`, whose window
 * is twice its stride (2e). But this endpoint is `LIMIT n OFFSET (page-1)*n` over a list that **grows
 * at the head**: a subscribed channel publishing between the two requests shifts every row down one,
 * so page 2 re-serves page 1's last row and the true row after it is never seen. Over a "Load more"
 * button pressed minutes apart on 30 channels that is ordinary, not exotic.
 *
 * Keeping the *first* occurrence is what makes the fold stable: the reader's list does not reorder
 * itself under them because something was published while they were reading. The row that gets pushed
 * off the end is lost until a refresh, which is the honest cost of offset paging over live data and is
 * why `duplicates` is reported rather than swallowed.
 *
 * The order is arrival order — deliberately **not** re-sorted across pages. Each page is internally
 * chronological and the pages are consecutive windows of one ordered list, so concatenation is already
 * correct; re-sorting would additionally hide the shift this dedupe exists to survive.
 */
export function foldSubscriptionFeed(
  pages: readonly SubscriptionFeedPage[],
  nowEpochSec: number,
): SubscriptionListing {
  const seen = new Set<string>();
  const rows: SubscriptionFeedRow[] = [];
  let duplicates = 0;
  let dropped = 0;

  for (const page of pages) {
    dropped += page.dropped.length;
    for (const row of page.rows) {
      if (seen.has(row.video.videoId)) {
        duplicates += 1;
        continue;
      }
      seen.add(row.video.videoId);
      rows.push(row);
    }
  }

  return {
    rows,
    notified: rows.filter((row) => row.notified).length,
    duplicates,
    dropped,
    // Over the videos actually held, for `playlistScrape`'s reason: the judgement is about what this
    // response carried, and a list assembled from three pages is still one list to a reader.
    scrape: feedScrape(
      rows.map((row) => row.video),
      nowEpochSec,
    ),
  };
}

/**
 * The next page to ask for, or `null` when the last one proved there is nothing after it.
 *
 * **"A short page is the last page" is sound here, and it is *not* sound for a channel tab** — the
 * same sentence, opposite verdicts, for a reason that is about the endpoint rather than about
 * politeness. A channel tab's page size is YouTube's to choose (2d measured 29 rows on page 7 of
 * twelve), so a short page says nothing. This one is a SQL window the request itself specified:
 * `max_results` rows come back unless the table ran out. Measured over five pages at 20 —
 * `20 · 20 · 20 · 20 · 6`, then `0` — with the two arrays summing to the requested size on every full
 * page.
 *
 * That earns a real request. `page=6` above returns `200` with two empty arrays and 32 bytes, so a
 * fold that stopped only on an empty page would end every complete feed with a Load-more button that
 * fetches and adds nothing — which is exactly the wart 2e's playlist pager could not close, because
 * there the equivalent proof needs a `videoCount` that is sometimes absent. Here the evidence is in
 * the page that already arrived.
 */
export function nextSubscriptionFeedPage(
  pages: readonly SubscriptionFeedPage[],
  maxResults: number = SUBSCRIPTION_FEED_PAGE_SIZE,
): number | null {
  if (pages.length === 0) return SUBSCRIPTION_FEED_FIRST_PAGE;

  const last = pages[pages.length - 1];
  if (last === undefined || last.malformed) return null;
  // Counted before the dedupe and before R4's drops: the question is how many rows the *instance*
  // put in the window, and a row we could not parse still filled a slot. Counting the survivors would
  // read one bad row as the end of the feed.
  const returned = last.rows.length + last.dropped.length;
  if (returned < Math.max(1, Math.floor(maxResults))) return null;

  return SUBSCRIPTION_FEED_FIRST_PAGE + pages.length;
}

/* ------------------------------------------------------------------------------------------------
 * What a feed row may state
 * ---------------------------------------------------------------------------------------------- */

/**
 * **No new per-field rule, and resisting one is the finding.** 2b's `feedScrape` and 2d's per-tab
 * facts both exist because an endpoint stated something false; this endpoint states nothing false, so
 * the honest answer is to add nothing. The three measurements behind that:
 *
 * - **`lengthSeconds: 0` on 34 of 86 rows**, and it is a real absence rather than 2d's constant: five
 *   of the zeros probed against `/api/v1/videos/{id}` are 65 s, 35 s, 66 s, 57 s and 89 s, while three
 *   rows that *do* report a duration report it exactly right (699/1371/697 s, matching upstream to the
 *   second). Every zero-length row is a genuine Short and every long row is fine, so Invidious's
 *   `channel_videos` table simply has no length for a Short. `feedRowHasDuration` already refuses `0`,
 *   so those rows draw no badge, which is the truth. Note the coincidence to *not* read into: every
 *   row's `type` is `"shortVideo"`, which is §2 trap 6's "abbreviated record" and says nothing about
 *   which rows are Shorts.
 * - **`viewCount` is real** (85 of 86 rows above zero) and **`published` is genuine, not
 *   back-computed** — 86 distinct times of day across 86 rows, so unlike search and
 *   `recommendedVideos[]` this endpoint's dates carry an absolute tooltip honestly.
 * - **There is no `liveNow`, `isUpcoming` or `premiereTimestamp` field at all**, so `videoBadgeOf`
 *   sees `false/false/null` and draws nothing for a live stream. That is the third form of the
 *   `liveNow` trap in three slices — 2d: `false` while live; 2e: `true` when deleted; here: absent —
 *   and it is the only one that needs no defence, because an absent field cannot lie. The row carries
 *   no `description` either (like a search row), and no `authorThumbnails`.
 *
 * So this function is a *statement* of the three, checkable by a test, rather than a filter in the
 * render path. It is what makes the measurements above fail loudly if the endpoint changes.
 */
export interface SubscriptionRowFacts {
  readonly hasDuration: boolean;
  readonly hasViewCount: boolean;
  readonly hasDate: boolean;
}

export function subscriptionRowFacts(
  video: VideoSummary,
  nowEpochSec: number,
): SubscriptionRowFacts {
  return {
    hasDuration: feedRowHasDuration(video),
    hasViewCount: feedRowHasViewCount(video),
    hasDate: feedRowHasDate(video, nowEpochSec),
  };
}

/* ------------------------------------------------------------------------------------------------
 * The notes a Subscriptions page has to make
 * ---------------------------------------------------------------------------------------------- */

/**
 * Something true about this feed that a reader cannot see by looking at it. R7: the ids are core's,
 * the sentences are the platform's.
 */
export type SubscriptionNote =
  /** Rows the instance has flagged. Carries the count, because the count is the whole content. */
  | { readonly kind: 'notified'; readonly count: number }
  /** R4 — rows that could not be read, so the list is short by a knowable amount. */
  | { readonly kind: 'dropped'; readonly count: number }
  /** The fold discarded repeats, which means the feed moved under the reader mid-page. */
  | { readonly kind: 'shifted'; readonly count: number }
  /** 2b's degeneracy, reachable here in principle and never observed on this endpoint. */
  | { readonly kind: 'titles-only'; readonly rows: number }
  /** Subscribed to nothing at all. The one empty state that is not a failure. */
  | { readonly kind: 'no-subscriptions' };

export function subscriptionNotes(
  listing: SubscriptionListing,
  subscriptionCount: number | null,
): readonly SubscriptionNote[] {
  const notes: SubscriptionNote[] = [];

  if (subscriptionCount === 0) notes.push({ kind: 'no-subscriptions' });
  if (listing.notified > 0) notes.push({ kind: 'notified', count: listing.notified });
  if (listing.scrape.degenerate) notes.push({ kind: 'titles-only', rows: listing.scrape.rows });
  if (listing.duplicates > 0) notes.push({ kind: 'shifted', count: listing.duplicates });
  if (listing.dropped > 0) notes.push({ kind: 'dropped', count: listing.dropped });

  return notes;
}
